import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerPanel } from '../ui/DrawerPanel';
import { LayoutConfig, getLayoutImage } from '../../constants/layouts';

interface CurrentModelDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  layout: LayoutConfig | null;
  /** Raw layout id, shown when the id is not in ALL_LAYOUTS (legacy/removed). */
  layoutId?: string;
}

/**
 * D10 (2026-08-10) — the CURRENT AidFone model only: one screenshot, one name.
 *
 * The screenshot used to sit in the hero card, where it inflated the card's
 * height on every page load. It moved behind this panel so the hero stays
 * compact and the image is still one tap away.
 *
 * Deliberately NOT the gallery. Browsing and switching models lives in the
 * "AidFone screen models" tile (SettingsDrawer); this panel answers only
 * "what is on the phone right now?" and offers no way to change it, so the two
 * surfaces do not compete.
 */
export const CurrentModelDrawer: FC<CurrentModelDrawerProps> = ({
  isOpen,
  onClose,
  layout,
  layoutId,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <DrawerPanel isOpen={isOpen} onClose={onClose} title={t('dashboard.heroCard.currentModel')}>
      <div className="flex flex-col items-center gap-4 py-2">
        {layout ? (
          <>
            <img
              src={getLayoutImage(layout, i18n.language)}
              alt={layout.name}
              width={412}
              height={910}
              className="w-auto max-h-[52vh] rounded-xl border border-brand-border shadow-sm object-contain"
            />
            <p className="font-display text-base font-semibold text-brand-text text-center">
              {layout.name}
            </p>
            {layout.description && (
              <p className="text-sm text-brand-text-muted text-center leading-relaxed">
                {layout.description}
              </p>
            )}
          </>
        ) : (
          /* Unknown id — a legacy or removed layout. Show the id rather than a
             broken image, so support can still see what the record holds. */
          <>
            <span
              className="material-symbols-outlined text-brand-text-muted/40"
              style={{ fontSize: '72px' }}
            >
              smartphone
            </span>
            <p className="font-display text-base font-semibold text-brand-text">
              {layoutId || '—'}
            </p>
          </>
        )}
      </div>
    </DrawerPanel>
  );
};

export default CurrentModelDrawer;
